import React, { createContext, useEffect, useState, useContext } from "react";
import axios from "axios";
import { API_URL } from "../config";
import { initSocket, disconnectSocket } from "../socket";

export const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

const ACTIONS = ["view", "add", "edit", "delete"];
const ACTION_INDEX_MAP = {
  0: "view",
  1: "add",
  2: "edit",
  3: "delete",
};

const toCleanModuleKey = (key = "") =>
  String(key).toLowerCase().replace(/\s+/g, "").replace(/[^a-z]/g, "");

const toPermissionObject = (value) => {
  const out = { view: false, add: false, edit: false, delete: false };

  if (Array.isArray(value)) {
    value.forEach((item) => {
      const str = String(item).trim().toLowerCase();
      const mapped = ACTION_INDEX_MAP[str] || str;
      if (ACTIONS.includes(mapped)) {
        out[mapped] = true;
      }
    });
    return out;
  }

  if (value && typeof value === "object") {
    ACTIONS.forEach((action) => {
      out[action] = value[action] === true;
    });
    return out;
  }

  return out;
};

const normalizePermissions = (permissions = {}) => {
  const normalized = {};

  Object.keys(permissions || {}).forEach((key) => {
    const cleanKey = toCleanModuleKey(key);
    normalized[cleanKey] = toPermissionObject(permissions[key]);
  });

  return normalized;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    axios
      .get(`${API_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const fetchedUser = res.data.user;

        setUser({
          ...fetchedUser,
          permissions: normalizePermissions(fetchedUser.permissions),
        });
      })
      .catch(() => {
        localStorage.removeItem("token");
        setUser(null);
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const login = ({ token, user }) => {
    localStorage.setItem("token", token);

    setToken(token);
    setUser({
      ...user,
      permissions: normalizePermissions(user.permissions),
    });
  };

  const logout = () => {
    localStorage.removeItem("token");
    disconnectSocket();
    setUser(null);
    setToken(null);
  };

  useEffect(() => {
    if (!token || !user) return;

    const socket = initSocket(token);

    if (user.role === "admin") {
      socket.emit("join-admin");
    }

    if (user.role) {
      socket.emit("join-role", user.role);
    }

    socket.on("force-logout", (data) => {
      logout();
      window.location.href = `/login?reason=${encodeURIComponent(data.reason || "session_ended")}`;
    });

    return () => {
      socket.off("force-logout");
    };
  }, [token, user]);

  const [breakStatus, setBreakStatus] = useState("Working"); // "Working" or "On Break"
  const [breakData, setBreakData] = useState({
    breakStart: null,
    breakEnd: null,
    totalBreakDuration: 0,
    breaks: []
  });

  const fetchTodayAttendance = async () => {
    if (!user || user.role === "admin" || user.role === "superadmin") return;
    try {
      const res = await axios.get(`${API_URL}/api/today`, {
        params: { employeeId: user._id }
      });
      if (res.data) {
        if (res.data.workingHours !== undefined) {
          setWorkingHours(res.data.workingHours);
        }
        if (res.data.breakStatus) {
          setBreakStatus(res.data.breakStatus);
        }
        setBreakData({
          breakStart: res.data.breakStart || null,
          breakEnd: res.data.breakEnd || null,
          totalBreakDuration: res.data.totalBreakDuration || 0,
          breaks: res.data.breaks || []
        });
      }
    } catch (err) {
      console.error("Failed to fetch today's attendance:", err);
    }
  };

  useEffect(() => {
    fetchTodayAttendance();
  }, [user]);

  const triggerStartBreak = async () => {
    if (!user?._id) return;
    try {
      const res = await axios.post(`${API_URL}/api/break/start`, { employeeId: user._id });
      if (res.data && res.data.attendance) {
        setBreakStatus("On Break");
        setBreakData({
          breakStart: res.data.attendance.breakStart,
          breakEnd: res.data.attendance.breakEnd,
          totalBreakDuration: res.data.attendance.totalBreakDuration,
          breaks: res.data.attendance.breaks || []
        });
      }
      return res.data;
    } catch (err) {
      console.error("Failed to start break:", err);
      throw err;
    }
  };

  const triggerResumeBreak = async () => {
    if (!user?._id) return;
    try {
      const res = await axios.post(`${API_URL}/api/break/resume`, { employeeId: user._id });
      if (res.data && res.data.attendance) {
        setBreakStatus("Working");
        setWorkingHours(res.data.attendance.workingHours);
        setBreakData({
          breakStart: res.data.attendance.breakStart,
          breakEnd: res.data.attendance.breakEnd,
          totalBreakDuration: res.data.attendance.totalBreakDuration,
          breaks: res.data.attendance.breaks || []
        });
      }
      return res.data;
    } catch (err) {
      console.error("Failed to resume break:", err);
      throw err;
    }
  };

  // --- Inactivity Lock System ---
  useEffect(() => {
    if (!user || user.role === "admin" || user.role === "superadmin" || breakStatus === "On Break") return;

    let timeout;
    // Use user-specific inactivityTimeout (provided in seconds) converted to ms
    // Fallback to 300s (5m) if not set
    const timeoutSeconds = user.inactivityTimeout || 300;
    const INACTIVITY_TIME = timeoutSeconds * 1000;

    const lockAccount = async () => {
      try {
        await axios.post(`${API_URL}/api/lock-inactivity`, { employeeId: user._id });
        logout();
        window.location.href = "/login?locked=true";
      } catch (err) {
        console.error("Failed to lock account", err);
        logout();
      }
    };

    const resetTimer = () => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(lockAccount, INACTIVITY_TIME);
    };

    const events = ["mousemove", "mousedown", "keypress", "scroll", "touchstart"];
    events.forEach((event) => window.addEventListener(event, resetTimer));

    resetTimer();

    return () => {
      if (timeout) clearTimeout(timeout);
      events.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [user, breakStatus]);

  const [workingHours, setWorkingHours] = useState(0);
  const [isTracking, setIsTracking] = useState(false);

  // Shared timer to increment workingHours globally while tracking is active
  useEffect(() => {
    let timer;
    if (isTracking && user && breakStatus !== "On Break") {
      timer = setInterval(() => {
        setWorkingHours((prev) => prev + 1 / 3600); // add 1 second (1/3600 hours)
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isTracking, user, breakStatus]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        loading,
        workingHours,
        setWorkingHours,
        isTracking,
        setIsTracking,
        breakStatus,
        isOnBreak: breakStatus === "On Break",
        breakData,
        fetchTodayAttendance,
        startBreak: triggerStartBreak,
        resumeBreak: triggerResumeBreak
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
